/**
 * Related-guides helper.
 *
 * Returns an ordered list of guide suggestions for the bottom-of-page
 * "Suggested Guides" section in GuideViewer.
 *
 * Rule:
 *   1. The FIRST suggestion is always the per-vehicle Overall Vehicle Inspection
 *      guide (mapped via lib/guideMaintenanceMap.js). If the active guide IS the
 *      inspection guide, this slot is skipped and we fall through to related-by-score.
 *   2. Remaining slots are filled by relevance score against the current guide.
 *
 * Scoring (higher = more relevant):
 *   +10  same category
 *   +5   same tab type (maintenance / repair / inspection)
 *   +4   same engine (only if both guides specify one)
 *   +2   same difficulty
 *   +1   shares any keyword token in title
 */

import { findGuideForMaintenanceItem } from './guideMaintenanceMap.js';
import { guideMatchesVehicle } from './guideMatching.js';
import { getGuideTabType } from './guideTabType.js';

const STOP_WORDS = new Set([
  'and', 'or', 'the', 'a', 'an', 'of', 'for', 'to', 'in', 'on', 'with',
  'replacement', 'change', 'service', 'inspection', 'check', 'repair',
]);

function tokenize(s) {
  return (s || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(t => t.length >= 3 && !STOP_WORDS.has(t));
}

function scoreRelevance(current, candidate) {
  let score = 0;
  if (current.category && current.category === candidate.category) score += 10;
  if (getGuideTabType(current) === getGuideTabType(candidate)) score += 5;
  if (current.vehicle?.engine && candidate.vehicle?.engine && current.vehicle.engine === candidate.vehicle.engine) score += 4;
  if (current.difficulty && current.difficulty === candidate.difficulty) score += 2;

  const a = new Set(tokenize(current.title));
  for (const t of tokenize(candidate.title)) {
    if (a.has(t)) { score += 1; break; }
  }
  return score;
}

/**
 * Build a vehicle context object from a guide for use with the matcher.
 * The matcher expects a "vehicle" with make/model/year/engine; guides have
 * generations[]. We synthesize a representative year so guideMatchesVehicle works.
 */
function vehicleContextFromGuide(guide) {
  if (!guide?.vehicle) return null;
  const v = guide.vehicle;
  let year;
  if (v.generations && v.generations.length) year = v.generations[0].startYear;
  else if (v.yearRange) year = v.yearRange.start;
  else year = v.year;
  return {
    make: v.make,
    model: v.model,
    year,
    engine: v.engine,
    transmission: v.transmission,
  };
}

/**
 * Get a list of suggested guides for the bottom-of-page section.
 * @param {object} currentGuide — the guide currently being viewed
 * @param {object[]} allGuides — full library
 * @param {number} limit — total suggestions to return (default 4)
 * @returns {object[]} ordered suggestions; first is Overall Vehicle Inspection (when applicable)
 */
export function getSuggestedGuides(currentGuide, allGuides, limit = 4) {
  if (!currentGuide || !Array.isArray(allGuides)) return [];

  const vehicleCtx = vehicleContextFromGuide(currentGuide);
  const result = [];
  const seen = new Set([currentGuide.id]);

  // 1. ALWAYS-FIRST slot: Overall Vehicle Inspection for the current vehicle.
  //    Skip if we're already viewing that guide.
  const inspection = vehicleCtx
    ? findGuideForMaintenanceItem('overall-vehicle-inspection', vehicleCtx, allGuides)
    : null;
  if (inspection && !seen.has(inspection.id)) {
    result.push({ ...inspection, _suggestedReason: 'inspection' });
    seen.add(inspection.id);
  }

  // 2. Score remaining candidates that match the same vehicle.
  const scored = [];
  for (const g of allGuides) {
    if (seen.has(g.id)) continue;
    if (vehicleCtx && !guideMatchesVehicle(g, vehicleCtx)) continue;
    const score = scoreRelevance(currentGuide, g);
    if (score > 0) scored.push({ guide: g, score });
  }

  scored.sort((a, b) => b.score - a.score);

  for (const { guide } of scored) {
    if (result.length >= limit) break;
    result.push({ ...guide, _suggestedReason: 'related' });
  }

  return result;
}
